import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, TrendingDown, AlertTriangle, Lock, BarChart3,
  ChevronRight, ArrowUpRight, ArrowDownRight, Minus, Upload, BookOpen
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import api from '../lib/api';

const fmtRs = (v) => `Rs. ${Number(v || 0).toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtPct = (v) => `${(v * 100).toFixed(1)}%`;

const StrengthBadge = ({ strength, direction }) => {
  let bg = '#e2e8f0';
  let color = '#64748b';
  if (strength === 'Strong') {
    bg = direction === 'positive' ? '#dcfce7' : '#fee2e2';
    color = direction === 'positive' ? '#16a34a' : '#dc2626';
  } else if (strength === 'Moderate') {
    bg = direction === 'positive' ? '#ecfdf5' : '#fef2f2';
    color = direction === 'positive' ? '#15803d' : '#b91c1c';
  }
  return (
    <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: bg, color }}>
      {strength} {direction === 'positive' ? 'Positive' : 'Negative'}
    </span>
  );
};

const CorrelationBar = ({ r }) => {
  const pct = Math.min(Math.max(Math.abs(r) * 100, 0), 100);
  const color = r >= 0 ? '#16a34a' : '#dc2626';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
      <div style={{ flex: 1, height: 8, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.3s' }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, color, minWidth: 50, textAlign: 'right' }}>{fmtPct(r)}</span>
    </div>
  );
};

const RegressionReport = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [readiness, setReadiness] = useState(null);
  const [report, setReport] = useState(null);

  useEffect(() => {
    const t = localStorage.getItem('token');
    const u = localStorage.getItem('user');
    if (!t || !u) { navigate('/login'); return; }
    setUser(JSON.parse(u));
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await api.get('/regression/data-readiness');
      setReadiness(r.data);
      if (r.data.is_ready) {
        const rep = await api.get('/regression/report');
        setReport(rep.data);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  // ==================== LOCKED STATE ====================
  const renderLocked = () => {
    const months = readiness?.months_available || 0;
    const needed = readiness?.months_needed || 6;
    const progress = Math.min((months / 6) * 100, 100);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', padding: 40 }}>
        <div style={{
          width: 80, height: 80, borderRadius: 24,
          background: 'linear-gradient(135deg, #0d9488 0%, #7c3aed 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 24, boxShadow: '0 10px 40px rgba(13,148,136,0.25)'
        }}>
          <Lock size={36} color="#fff" />
        </div>

        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1e293b', marginBottom: 8, textAlign: 'center' }}>
          Your Business Intelligence Report
        </h1>
        <p style={{ fontSize: 15, color: '#64748b', marginBottom: 32, textAlign: 'center', maxWidth: 480 }}>
          Unlock deeper insights into what drives your business growth
        </p>

        <div style={{ width: '100%', maxWidth: 420, marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 8 }}>
            <span>Data Collection Progress</span>
            <span>{months}/6 months</span>
          </div>
          <div style={{ width: '100%', height: 10, background: '#e2e8f0', borderRadius: 5, overflow: 'hidden' }}>
            <div style={{ width: `${progress}%`, height: '100%', background: 'linear-gradient(90deg, #0d9488, #7c3aed)', borderRadius: 5, transition: 'width 0.5s' }} />
          </div>
          <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>
            {months} of 6 months of transaction data collected
          </p>
        </div>

        <div style={{ background: '#f8fafc', borderRadius: 12, padding: 20, maxWidth: 420, width: '100%', marginBottom: 24 }}>
          <p style={{ fontSize: 14, color: '#475569', lineHeight: 1.6, marginBottom: 12 }}>
            <strong style={{ color: '#1e293b' }}>Your Regression Report is Almost Ready</strong>
          </p>
          <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6, marginBottom: 12 }}>
            We need at least 6 months of data to generate accurate insights. You currently have <strong>{months} month{months !== 1 ? 's' : ''}</strong> of data.
          </p>
          <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
            <strong>Tip:</strong> You can import your historical data using our Excel Import feature to unlock this instantly.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={() => navigate('/dashboard/settings/opening-balances')}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '12px 24px', borderRadius: 10, border: 'none',
              background: 'linear-gradient(135deg, #0d9488, #7c3aed)',
              color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer'
            }}
          >
            <Upload size={16} /> Import Historical Data
          </button>
          <button
            onClick={() => alert('Regression Analysis uses Pearson correlation to identify which business variables most strongly predict revenue, profit, cash flow, and receivables. It requires at least 6 months of consistent transaction data to produce statistically meaningful results.')}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '12px 24px', borderRadius: 10, border: '1px solid #cbd5e1',
              background: '#fff', color: '#475569', fontSize: 14, fontWeight: 500, cursor: 'pointer'
            }}
          >
            <BookOpen size={16} /> Learn More
          </button>
        </div>
      </div>
    );
  };

  // ==================== READY STATE ====================
  const renderReady = () => {
    if (!report) return null;
    const months = report.months_analyzed;

    // Build correlation matrix for display
    const matrix = {};
    report.correlations.forEach(c => {
      if (!matrix[c.independent_var]) matrix[c.independent_var] = {};
      matrix[c.independent_var][c.dependent_var] = c;
    });

    const depOrder = ['Revenue', 'Net Profit', 'Cash Balance', 'Receivables'];

    return (
      <div>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 22, fontWeight: 600, color: '#1e293b', marginBottom: 4 }}>Regression Analysis Report</h1>
          <p style={{ fontSize: 13, color: '#64748b' }}>
            Based on {months} months of your actual business data ({report.earliest_date} to {report.latest_date})
          </p>
        </div>

        {/* Top Revenue Drivers */}
        {report.top_revenue_drivers?.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 14, fontWeight: 600, color: '#0f766e', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <TrendingUp size={16} /> Top Revenue Drivers
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
              {report.top_revenue_drivers.map((item, i) => (
                <div key={i} style={{
                  background: 'linear-gradient(135deg, #f0fdfa 0%, #e0f2fe 100%)',
                  borderRadius: 12, padding: 20, border: '1px solid #ccfbf1'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#134e4a' }}>{item.independent_var}</span>
                    <ArrowUpRight size={16} color="#0d9488" />
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#0f766e', marginBottom: 8 }}>{fmtPct(item.r_value)}</div>
                  <StrengthBadge strength={item.strength} direction={item.direction} />
                  <p style={{ fontSize: 12, color: '#475569', marginTop: 10, lineHeight: 1.5 }}>{item.insight_text}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top Profit Drivers */}
        {report.top_profit_drivers?.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 14, fontWeight: 600, color: '#7c3aed', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <TrendingUp size={16} /> Top Profit Drivers
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
              {report.top_profit_drivers.map((item, i) => (
                <div key={i} style={{
                  background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
                  borderRadius: 12, padding: 20, border: '1px solid #e9d5ff'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#581c87' }}>{item.independent_var}</span>
                    <ArrowUpRight size={16} color="#7c3aed" />
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#7c3aed', marginBottom: 8 }}>{fmtPct(item.r_value)}</div>
                  <StrengthBadge strength={item.strength} direction={item.direction} />
                  <p style={{ fontSize: 12, color: '#475569', marginTop: 10, lineHeight: 1.5 }}>{item.insight_text}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Risk Factors */}
        {report.biggest_risks?.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 14, fontWeight: 600, color: '#dc2626', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <AlertTriangle size={16} /> Risk Factors
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
              {report.biggest_risks.map((item, i) => (
                <div key={i} style={{
                  background: 'linear-gradient(135deg, #fef2f2 0%, #fff7ed 100%)',
                  borderRadius: 12, padding: 20, border: '1px solid #fecaca'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#7f1d1d' }}>{item.independent_var}</span>
                    <ArrowDownRight size={16} color="#dc2626" />
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#dc2626', marginBottom: 8 }}>{fmtPct(item.r_value)}</div>
                  <StrengthBadge strength={item.strength} direction={item.direction} />
                  <p style={{ fontSize: 12, color: '#475569', marginTop: 10, lineHeight: 1.5 }}>{item.insight_text}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Full Correlation Matrix */}
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20, marginBottom: 24 }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, color: '#475569', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
            <BarChart3 size={16} /> Full Correlation Matrix
          </h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0' }}>Variable</th>
                  {depOrder.map(dep => (
                    <th key={dep} style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0', minWidth: 140 }}>{dep}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.keys(matrix).map((indVar, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 500, color: '#1e293b' }}>{indVar}</td>
                    {depOrder.map(dep => {
                      const cell = matrix[indVar]?.[dep];
                      if (!cell) return <td key={dep} style={{ padding: '10px 12px', textAlign: 'center', color: '#cbd5e1' }}>—</td>;
                      const color = cell.r_value > 0 ? '#16a34a' : cell.r_value < 0 ? '#dc2626' : '#94a3b8';
                      const bg = cell.strength === 'Strong' ? (cell.r_value > 0 ? '#f0fdf4' : '#fef2f2') : 'transparent';
                      return (
                        <td key={dep} style={{ padding: '10px 12px', textAlign: 'center', background: bg }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color }}>{cell.r_value > 0 ? '+' : ''}{fmtPct(cell.r_value)}</span>
                            {cell.r_value > 0 ? <ArrowUpRight size={12} color={color} /> : cell.r_value < 0 ? <ArrowDownRight size={12} color={color} /> : <Minus size={12} color={color} />}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Key Insights */}
        {report.correlations?.length > 0 && (
          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20 }}>
            <h2 style={{ fontSize: 14, fontWeight: 600, color: '#475569', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
              <BookOpen size={16} /> Key Insights
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {report.correlations.map((c, i) => (
                <div key={i} style={{
                  padding: 14, borderRadius: 10,
                  background: c.direction === 'positive' ? '#f0fdf4' : '#fef2f2',
                  border: `1px solid ${c.direction === 'positive' ? '#bbf7d0' : '#fecaca'}`
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <StrengthBadge strength={c.strength} direction={c.direction} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>
                      {c.independent_var} → {c.dependent_var}
                    </span>
                  </div>
                  <p style={{ fontSize: 13, color: '#334155', lineHeight: 1.5 }}>{c.insight_text}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f1f5f9' }}>
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} user={user} />
      <div style={{ flex: 1, marginLeft: isSidebarOpen ? 256 : 0, transition: 'margin-left 0.3s' }}>
        <Navbar user={user} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <main style={{ padding: 24 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Loading report...</div>
          ) : readiness?.is_ready ? (
            renderReady()
          ) : (
            renderLocked()
          )}
        </main>
      </div>
    </div>
  );
};

export default RegressionReport;
